import { createFileRoute } from '@tanstack/react-router'
import { Boundary, QueryParamBuilder, type FilterMenuInput, Page } from '~/components'
import { z } from 'zod'
import { FilesTable } from '~/features/files/components'
import { Spinner } from '~/elements'
import { FILTER_SEPARATOR } from '~/components/QueryParamBuilder/parseParams'
import { useSuspenseInfiniteQuery } from '@tanstack/react-query'
import { listFilesInfiniteQuery } from '~/queries'
import { useInfiniteScroll } from '~/hooks'

const filesSearchSchema = z.object({
  tags: z
    .record(z.string())
    .optional()
    .catch(() => undefined),
  sortDirection: z
    .enum(['asc', 'desc'])
    .optional()
    .catch(() => undefined),
  sortField: z
    .enum(['size', 'updatedAt', 'status', 'key', 'createdAt'])
    .optional()
    .catch(() => undefined),
})

export const Route = createFileRoute('/workspaces/$workspaceId/bots/$botId/files')({
  validateSearch: filesSearchSchema,
  component: Component,
})

function Component() {
  return (
    <Boundary height={'200px'} loaderSize="6">
      <FilesPage />
    </Boundary>
  )
}

const FilesPage = () => {
  const filters: FilterMenuInput[] = [
    {
      name: 'Source',
      type: 'object',
      paramName: 'tags',
      value: [
        { name: 'Integration', paramName: `tags${FILTER_SEPARATOR}source`, value: 'integration' },
        { name: 'Knowledge Base', paramName: `tags${FILTER_SEPARATOR}source`, value: 'knowledge-base' },
      ],
    },
    { name: 'Title', type: 'object', paramName: `tags${FILTER_SEPARATOR}title` },
    { name: 'Knowledge Base Id', type: 'object', paramName: `tags${FILTER_SEPARATOR}kbId` },
    { name: 'Data Source Id', type: 'object', paramName: `tags${FILTER_SEPARATOR}dsId` },
    {
      name: 'System',
      type: 'object',
      paramName: `tags${FILTER_SEPARATOR}system`,
      value: 'true',
    },
    {
      name: 'Integration',
      type: 'object',
      paramName: `tags${FILTER_SEPARATOR}integration`,
    },
    {
      name: 'Tag',
      type: 'object',
      paramName: `tags`,
    },
  ]

  return (
    <Page title="Files">
      <div className="flex flex-col gap-4">
        <QueryParamBuilder filters={filters} />
        <Boundary loaderSize={'6'}>
          <FilesList />
        </Boundary>
      </div>
    </Page>
  )
}

const FilesList = () => {
  const { workspaceId, botId } = Route.useParams()
  const { tags, sortDirection, sortField } = Route.useSearch()
  const {
    fetchNextPage,
    hasNextPage,
    isFetching,
    data: fetchedFiles,
  } = useSuspenseInfiniteQuery(listFilesInfiniteQuery({ botId, workspaceId, sortDirection, sortField, tags }))

  const files = fetchedFiles.pages?.flatMap((page) => page.files) ?? []

  const bottomRef = useInfiniteScroll(fetchNextPage, hasNextPage)

  return (
    <>
      <FilesTable workspaceId={workspaceId} botId={botId} files={files} />
      <div ref={bottomRef} />
      {isFetching && (
        <div className="flex w-full items-center justify-center">
          <Spinner size="6" />
        </div>
      )}
    </>
  )
}
